import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ShieldCheck, Check, Lock } from 'lucide-react';
import { API_BASE_URL } from '../utils/env';
import { BRAND } from '@/brand';

const ACCENT = BRAND === 'corporate' ? '#E8890B' : '#1976D2';
const ACCENT_D = BRAND === 'corporate' ? '#C76B12' : '#0B4CA0';
const BRAND_NAME = BRAND === 'corporate' ? 'Run & Comply' : 'Grow & Raise';
const CLIENT_HOME = BRAND === 'corporate' ? '/' : '/';

type Phase = 'checking' | 'form' | 'invitation' | 'done';

export default function AdminSignup() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('checking');
  const [name, setName] = useState('');
  const [entity, setEntity] = useState<'corporate' | 'consulting'>(BRAND === 'corporate' ? 'corporate' : 'consulting');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/auth/admin/exists`);
        const d = await r.json();
        setPhase(d?.exists ? 'invitation' : 'form');
      } catch {
        setPhase('invitation'); // fail safe — assume provisioned
      }
    })();
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim()) { setError('Please fill in your name and email.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/auth/admin/bootstrap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), password, entity }),
      });
      const d = await r.json();
      if (r.status === 409) { setPhase('invitation'); return; }
      if (!r.ok || !d.success) throw new Error(d.error || 'Could not create the owner account.');
      setPhase('done');
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--acc)]/25 focus:border-[var(--acc)]';

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1fr]" style={{ ['--acc' as any]: ACCENT, fontFamily: "'Montserrat',system-ui,sans-serif" }}>
      {/* Left — form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2 mb-8">
            <span className="w-9 h-9 rounded-xl grid place-items-center text-white" style={{ background: ACCENT }}><ShieldCheck className="w-5 h-5" /></span>
            <div>
              <div className="text-[15px] font-black text-slate-900 leading-none">Conncct <span style={{ color: ACCENT }}>Admin</span></div>
              <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-slate-400 mt-1">{BRAND_NAME}</div>
            </div>
          </div>

          {phase === 'checking' && (
            <div className="py-16 flex items-center gap-3 text-slate-400 font-medium"><div className="w-5 h-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" /> Checking workspace…</div>
          )}

          {phase === 'form' && (
            <>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Create the owner account</h1>
              <p className="text-sm text-slate-500 mt-1.5 mb-7">No admin exists for {BRAND_NAME} yet. This first account owns the workspace and can invite the rest of the team.</p>
              {error && <div className="mb-4 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
              <form onSubmit={submit} className="space-y-4">
                <div><label className="block text-xs font-bold text-slate-600 mb-1.5">Your name</label><input className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="Full name" autoComplete="name" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1.5">Workspace</label>
                  <select className={inputCls} value={entity} onChange={e => setEntity(e.target.value as any)}>
                    <option value="corporate">Run &amp; Comply (Accounting &amp; Corporate)</option>
                    <option value="consulting">Grow &amp; Raise (Consulting)</option>
                  </select></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1.5">Work email</label><input type="email" className={inputCls} value={email} onChange={e => setEmail(e.target.value)} placeholder="you@conncct.com" autoComplete="email" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1.5">Create a password</label>
                  <div className="relative">
                    <input type={show ? 'text' : 'password'} className={inputCls + ' pr-10'} value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" />
                    <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1.5">Confirm password</label><input type={show ? 'text' : 'password'} className={inputCls} value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" /></div>
                <button type="submit" disabled={loading} className="w-full rounded-xl py-3 text-sm font-bold text-white disabled:opacity-60" style={{ background: ACCENT }}>{loading ? 'Creating…' : 'Create owner account'}</button>
              </form>
              <p className="text-sm text-slate-500 mt-6">Already have an account? <Link to="/admin-login" className="font-bold" style={{ color: ACCENT }}>Sign in →</Link></p>
            </>
          )}

          {phase === 'invitation' && (
            <>
              <div className="w-12 h-12 rounded-2xl grid place-items-center mb-5" style={{ background: `${ACCENT}1a`, color: ACCENT }}><Lock className="w-6 h-6" /></div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Admin accounts are by invitation</h1>
              <p className="text-sm text-slate-500 mt-1.5 mb-7">The {BRAND_NAME} workspace is already set up. New admins are added by an existing owner from inside the panel: there's no open sign‑up.</p>
              <Link to="/admin-login" className="inline-flex items-center justify-center w-full rounded-xl py-3 text-sm font-bold text-white" style={{ background: ACCENT }}>Sign in to the admin</Link>
              <p className="text-sm text-slate-500 mt-6">Looking for the client side? <Link to={CLIENT_HOME} className="font-bold" style={{ color: ACCENT }}>Go to {BRAND_NAME} →</Link></p>
            </>
          )}

          {phase === 'done' && (
            <>
              <div className="w-14 h-14 rounded-full grid place-items-center mb-5 bg-green-50 text-green-600"><Check className="w-7 h-7" /></div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Owner account created</h1>
              <p className="text-sm text-slate-500 mt-1.5 mb-7">You're the owner of the {BRAND_NAME} workspace. Sign in with your email and password to open the admin.</p>
              <button onClick={() => navigate('/admin-login')} className="w-full rounded-xl py-3 text-sm font-bold text-white" style={{ background: ACCENT }}>Sign in to the admin →</button>
            </>
          )}
        </div>
      </div>

      {/* Right — brand rail */}
      <div className="hidden lg:flex flex-col justify-center px-16 text-white relative overflow-hidden" style={{ background: `linear-gradient(150deg, #17263A, #0E1A2B)` }}>
        <div aria-hidden style={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', top: -140, right: -120, background: `radial-gradient(circle, ${ACCENT}55, transparent 70%)`, filter: 'blur(14px)' }} />
        <div className="relative z-10 max-w-md">
          <div className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: ACCENT === '#1976D2' ? '#8fc0f5' : '#F5B841' }}>Conncct Admin · {BRAND_NAME}</div>
          <h2 className="text-3xl font-black tracking-tight mt-3 leading-tight">One panel to run the whole back office.</h2>
          <p className="text-[15px] text-slate-300 mt-4 leading-relaxed">Clients, orders, documents, filings, pricing and content, managed and delivered by your team from a single place.</p>
          <ul className="mt-8 space-y-3">
            {['Deliver documents & compliance calendars', 'Manage orders, billing and clients', 'Edit pricing & content, live to the site', 'Invite your team with the right access'].map(t => (
              <li key={t} className="flex items-center gap-3 text-sm text-slate-200"><span className="w-5 h-5 rounded-full grid place-items-center flex-none" style={{ background: ACCENT }}><Check className="w-3 h-3 text-white" /></span>{t}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
